import axios from "axios";
import { useCallback } from "react";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import { useEffect, useState, useRef } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../utils/TableCustomStyles";

function VesselMaster() {
  const addform = useRef(null);
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [data, setData] = useState([]);
  const [dataPending, setDataPending] = useState(true);
  const [currentUserRolePermissionData, setCurrentUserRolePermissionData] =
    useState([]);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const user_role = sessionStorage["userrole"];
  const user_id = sessionStorage["id"];
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [canvasFullScreen, setCanvasFullScreen] = useState(false);
  const [showAddCanvas, setShowAddCanvas] = useState(false);
  const [showUpdateCanvas, setShowUpdateCanvas] = useState(false);

  // Add form state
  const [name, setName] = useState("");
  const [type, setType] = useState("SHIP");
  const [status, setStatus] = useState("ACTIVE");

  // Add field errors
  const [showNameError, setShowNameError] = useState(false);
  const [showVesselNameExistError, setShowVesselNameExistError] =
    useState(false);

  // Add validation summary
  const [showValidationError, setShowValidationError] = useState(false);
  const [isValidationErrorVisible, setIsValidationErrorVisible] =
    useState(false);

  // Update form state
  const [updateId, setUpdateId] = useState("");
  const [updateName, setUpdateName] = useState("");
  const [updateType, setUpdateType] = useState("SHIP");
  const [updateStatus, setUpdateStatus] = useState("ACTIVE");

  // Update field errors
  const [showUpdateNameError, setShowUpdateNameError] = useState(false);
  const [showUpdateVesselNameExistError, setShowUpdateVesselNameExistError] =
    useState(false);

  // Update validation summary
  const [showUpdateValidationError, setShowUpdateValidationError] =
    useState(false);
  const [isUpdateValidationErrorVisible, setIsUpdateValidationErrorVisible] =
    useState(false);

  // Loaders
  const [showAddLoader, setShowAddLoader] = useState(false);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);

  // ========== Validation Helpers ==========
  const hasAnyAddError = () => showNameError || showVesselNameExistError;

  const buildAddErrorMessages = (overrides = {}) => {
    const errors = [];
    const n = "name" in overrides ? overrides.name : name;
    const exists =
      "exists" in overrides ? overrides.exists : showVesselNameExistError;
    if (!n) errors.push("Vessel name is required");
    if (exists) errors.push("Vessel name already exists");
    return errors;
  };

  const hasAnyUpdateError = () =>
    showUpdateNameError || showUpdateVesselNameExistError;

  const buildUpdateErrorMessages = (overrides = {}) => {
    const errors = [];
    const n = "updateName" in overrides ? overrides.updateName : updateName;
    const exists =
      "exists" in overrides ? overrides.exists : showUpdateVesselNameExistError;
    if (!n) errors.push("Vessel name is required");
    if (exists) errors.push("Vessel name already exists");
    return errors;
  };

  const handleCheckValidationClick = () =>
    setIsValidationErrorVisible(!isValidationErrorVisible);

  const handleUpdateCheckValidationClick = () =>
    setIsUpdateValidationErrorVisible(!isUpdateValidationErrorVisible);

  const vesselNameExists = (val) =>
    data.some((item) => item.name.toLowerCase() === val.toLowerCase());

  const vesselNameExistsForUpdate = (val) =>
    data.some(
      (item) =>
        item.name.toLowerCase() === val.toLowerCase() && item.id !== updateId,
    );

  // ========== API calls ==========
  const getLoggenInUser = useCallback(async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL + "/user/user/" + user_id,
      );
      if (response) {
        setLoggedInUser(response.data);
      }
    } catch (err) {
      toast.error("Error in fetching user data! Try again.");
    }
  }, [user_id]);

  const getVessel = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL + "/master/vessel/",
      );
      if (response) {
        setData(response.data);
        setDataPending(false);
      }
    } catch (err) {
      toast.error("Error in fetching vessel data! Try again.");
    }
  };

  const getCurrentUserRolePermission = async (roles) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL +
          "/user/role/getuserrolepermission?roles=" +
          roles,
      );

      const role_permissions_array = [];
      for (let i = 0; i < response.data.length; i++) {
        for (let j = 0; j < response?.data[i]?.permissions_array.length; j++) {
          if (
            !role_permissions_array.includes(
              response?.data[i]?.permissions_array?.[j],
            )
          ) {
            role_permissions_array.push(
              response?.data[i]?.permissions_array?.[j],
            );
          }
        }
      }

      if (role_permissions_array) {
        setCurrentUserRolePermissionData(role_permissions_array);
      }
    } catch (err) {
      toast.error("Error in fetching user role data! Try again.");
    }
  };

  useEffect(() => {
    getVessel();
    getLoggenInUser();
  }, [getLoggenInUser]);

  useEffect(() => {
    getCurrentUserRolePermission(user_role);
  }, [user_role]);

  // ========== Delete handlers ==========
  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const handleDeleteModalOpen = (row) => {
    setShowDeleteModal(true);
    setDeleteRecordId(row.id);
  };

  const deleteVessel = async () => {
    setShowDeleteLoader(true);
    try {
      await axios.delete(
        process.env.REACT_APP_BACKEND_API_URL +
          "/master/vessel/" +
          deleteRecordId +
          "/",
        { headers: { "content-type": "application/json" } },
      );
      toast.success("Vessel Deleted Successfully");
      getVessel();
    } catch (err) {
      toast.error("Error deleting vessel! Try again.");
    } finally {
      setShowDeleteLoader(false);
      setShowDeleteModal(false);
    }
  };

  // ========== Add logic ==========
  const resetAddForm = () => {
    setName("");
    setType("SHIP");
    setStatus("ACTIVE");
    setShowNameError(false);
    setShowVesselNameExistError(false);
    setShowValidationError(false);
    setIsValidationErrorVisible(false);
  };

  const handleAddModalClose = () => {
    resetAddForm();
    setShowAddCanvas(false);
    setCanvasFullScreen(false);
  };

  const addVessel = async (e) => {
    e.preventDefault();

    const exists = vesselNameExists(name);
    setShowNameError(!name);
    setShowVesselNameExistError(exists);

    if (!name || exists) {
      setShowValidationError(true);
      return;
    }

    setShowAddLoader(true);

    const payload = {
      name: name,
      type: type,
      status: status,
      created_by: JSON.stringify({"id": loggedInUser?.id, "username": loggedInUser?.username, "name": loggedInUser?.name, "email": loggedInUser?.email, "mobile": loggedInUser?.mobile, "user_type": loggedInUser?.user_type}),
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_API_URL + "/master/vessel/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 201) {
        toast.success("Vessel Created Successfully");
        resetAddForm();
        getVessel();
        setShowAddCanvas(false);
      }
    } catch (err) {
      toast.error("Error creating vessel! Try again.");
    } finally {
      setShowAddLoader(false);
    }
  };

  // ========== Update logic ==========
  const resetUpdateForm = () => {
    setUpdateId("");
    setUpdateName("");
    setUpdateType("SHIP");
    setUpdateStatus("ACTIVE");
    setShowUpdateNameError(false);
    setShowUpdateVesselNameExistError(false);
    setShowUpdateValidationError(false);
    setIsUpdateValidationErrorVisible(false);
  };

  const handleUpdateModalClose = () => {
    resetUpdateForm();
    setShowUpdateCanvas(false);
    setCanvasFullScreen(false);
  };

  const handleSetUpdateData = (row) => {
    setUpdateId(row.id);
    setUpdateName(row.name);
    setUpdateType(row.type);
    setUpdateStatus(row.status);
    setShowUpdateNameError(false);
    setShowUpdateVesselNameExistError(false);
    setShowUpdateValidationError(false);
    setIsUpdateValidationErrorVisible(false);
  };

  const updateVessel = async (e) => {
    e.preventDefault();

    const exists = vesselNameExistsForUpdate(updateName);
    setShowUpdateNameError(!updateName);
    setShowUpdateVesselNameExistError(exists);

    if (!updateName || exists) {
      setShowUpdateValidationError(true);
      return;
    }

    setShowUpdateLoader(true);

    const payload = {
      name: updateName,
      type: updateType,
      status: updateStatus,
      modified_by: JSON.stringify({"id": loggedInUser?.id, "username": loggedInUser?.username, "name": loggedInUser?.name, "email": loggedInUser?.email, "mobile": loggedInUser?.mobile, "user_type": loggedInUser?.user_type}),
    };

    try {
      const response = await axios.put(
        process.env.REACT_APP_BACKEND_API_URL +
          "/master/vessel/" +
          updateId +
          "/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 200) {
        toast.success("Vessel Updated Successfully");
        handleUpdateModalClose();
        getVessel();
      }
    } catch (err) {
      toast.error("Error updating vessel! Try again.");
    } finally {
      setShowUpdateLoader(false);
    }
  };

  // ========== get_user safe parser ==========
  const get_user = (userjson) => {
    if (!userjson) return "None";
    try {
      const user_data = JSON.parse(userjson);
      return user_data?.name || "None";
    } catch {
      return "None";
    }
  };

  function handleDataFromTopNavBar(permissiondata) {
    setDataFromTopNavBar(permissiondata);
  }

  // ========== Table columns ==========
  const columns = [
    {
      name: "Vessel Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Vessel Type",
      selector: (row) => row.type,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row) => get_user(row.created_by),
      sortable: true,
    },
    {
      name: "Created On",
      selector: (row) => row.created_on.slice(0, 10),
      sortable: true,
    },
    {
      name: "Action",
      right: true,
      selector: (row) => (
        <div>
          {currentUserRolePermissionData?.includes("master:vessel:update") ? (
            <Button
              className="btn btn-sm btn-inox-green border-0"
              title="Update"
              data-bs-toggle="offcanvas"
              data-bs-target="#updateSection"
              onClick={() => handleSetUpdateData(row)}
            >
              <i className="fa fa-edit"></i>
            </Button>
          ) : (
            ""
          )}

          {currentUserRolePermissionData?.includes("master:vessel:delete") ? (
            <Button
              className="btn btn-sm bg-danger border-0 mx-2"
              title="Delete"
              onClick={() => handleDeleteModalOpen(row)}
            >
              <i className="fa fa-trash"></i>
            </Button>
          ) : (
            ""
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-content-bg">
      <ToastContainer></ToastContainer>
      <TopNavbar
        sidebarCollapsed={menuDrawerShow}
        topNavbarStatic={true}
        sendDataToParent={handleDataFromTopNavBar}
      />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="Master"
        activeSubMenu="VesselMaster"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div
        className={
          menuDrawerShow
            ? "row main-content-collapsed text-left py-0"
            : "row main-content text-left py-0"
        }
      >
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-6 py-1">
              <Button
                className="btn btn-sm btn-inox-green border-0 mx-2"
                onClick={() => setMenuDrawerShow(!menuDrawerShow)}
              >
                {menuDrawerShow ? (
                  <span>
                    <i className="fa fa-chevron-left mx-1"></i>
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-chevron-right mx-1"></i>
                  </span>
                )}
              </Button>
              <span className="page-header-text">Vessel Master</span>
            </div>

            <div className="col-6 py-1" style={{ paddingRight: 30 }}>
              {currentUserRolePermissionData?.includes("master:vessel:add") ? (
                <Button
                  className="btn btn-sm btn-inox-green border-0 px-3 float-right"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#addSection"
                >
                  <i
                    className="fa fa-plus fa-lg text-right"
                    style={{ paddingRight: 10 }}
                  ></i>
                  <span className="fw-bold">Add Vessel</span>
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div
          className="col-12 text-left"
          style={{
            marginTop: -5,
            paddingLeft: 20,
            paddingRight: 30,
            zIndex: 100,
          }}
        >
          <DataTable
            columns={columns}
            data={data}
            progressPending={dataPending}
            progressComponent={
              <Spinner
                className="mx-5 text-inox-green table-spinner"
                as="span"
                animation="border"
                size="lg"
                role="status"
                aria-hidden="true"
              />
            }
            noDataComponent={
              <p className="text-center table-no-data">No Data Found</p>
            }
            pagination
            highlightOnHover
            fixedHeader
            customStyles={tableCustomStyles}
            dense
          />
        </div>
      </div>

      {/* -------------------- Delete Modal -------------------- */}
      <Modal
        size="sm"
        show={showDeleteModal}
        onHide={handleDeleteModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this record?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button variant="danger" onClick={deleteVessel}>
            {showDeleteLoader ? (
              <Spinner
                className="mx-4"
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* -------------------- Update Canvas -------------------- */}
      <div
        className={
          canvasFullScreen && showUpdateCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasFullScreen && showUpdateCanvas
              ? "offcanvas offcanvas-bottom h-60 show"
              : "offcanvas offcanvas-bottom h-60"
        }
        id="updateSection"
        data-bs-scroll="true"
        tabIndex="-1"
        aria-labelledby="offcanvasBottomLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="offcanvasBottomLabel" className="float-left">
                Update Record
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                title="Close"
                onClick={handleUpdateModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>

              {canvasFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => [
                    setCanvasFullScreen(false),
                    setShowUpdateCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => [
                    setCanvasFullScreen(true),
                    setShowUpdateCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="offcanvas-body">
          <form onSubmit={(e) => updateVessel(e)}>
            <div className="row">
              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="update_name">
                  Vessel Name:{" "}
                  <span className="text-danger">
                    {showUpdateNameError ? "Vessel Name is required" : ""}
                    {showUpdateVesselNameExistError
                      ? "Vessel name already exists"
                      : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="update_name"
                  name="update_name"
                  placeholder="Enter Vessel Name"
                  value={updateName}
                  onChange={(e) => {
                    const val = e.target.value;
                    const exists = vesselNameExistsForUpdate(val);
                    setUpdateName(val);
                    setShowUpdateNameError(!val);
                    setShowUpdateVesselNameExistError(exists);
                    setShowUpdateValidationError(false);
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    const exists = vesselNameExistsForUpdate(val);
                    setShowUpdateNameError(!val);
                    setShowUpdateVesselNameExistError(exists);
                  }}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="update_type">Vessel Type:</label>
                <select
                  className="form-select form-select-sm"
                  id="update_type"
                  name="update_type"
                  value={updateType}
                  onChange={(e) => setUpdateType(e.target.value)}
                >
                  <option value="SHIP">SHIP</option>
                  <option value="TRAIN">TRAIN</option>
                  <option value="TRUCK">TRUCK</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="update_status">Status:</label>
                <select
                  className="form-select form-select-sm"
                  id="update_status"
                  name="update_status"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <Button
                  type="button"
                  className="btn btn-inox-green border-0 canvas-button btn-sm mt-4"
                  onClick={(e) => updateVessel(e)}
                  disabled={hasAnyUpdateError() || showUpdateLoader}
                >
                  {showUpdateLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-edit"></i>
                      <span className="mx-2">Update</span>
                    </>
                  )}
                </Button>

                {(hasAnyUpdateError() || showUpdateValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{
                        backgroundColor: "#C21E22",
                        border: "#f82e2e",
                      }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleUpdateCheckValidationClick}
                      onMouseLeave={() =>
                        setIsUpdateValidationErrorVisible(false)
                      }
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isUpdateValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {buildUpdateErrorMessages().map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* -------------------- End Update Canvas -------------------- */}

      {/* -------------------- Add Canvas -------------------- */}
      <div
        className={
          canvasFullScreen && showAddCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasFullScreen && showAddCanvas
              ? "offcanvas offcanvas-bottom h-60 show"
              : "offcanvas offcanvas-bottom h-60"
        }
        data-bs-scroll="true"
        tabIndex="-1"
        id="addSection"
        aria-labelledby="offcanvasBottomLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="offcanvasBottomLabel" className="float-left">
                Add New Record
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                title="Close"
                onClick={handleAddModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>

              {canvasFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => [
                    setCanvasFullScreen(false),
                    setShowAddCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => [
                    setCanvasFullScreen(true),
                    setShowAddCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="offcanvas-body">
          <form ref={addform} onSubmit={(e) => addVessel(e)}>
            <div className="row">
              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="add_name">
                  Vessel Name:{" "}
                  <span className="text-danger">
                    {showNameError ? "Vessel name is required" : ""}
                    {showVesselNameExistError
                      ? "Vessel name already exists"
                      : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="add_name"
                  name="add_name"
                  placeholder="Enter Vessel Name"
                  value={name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const exists = vesselNameExists(val);
                    setName(val);
                    setShowNameError(!val);
                    setShowVesselNameExistError(exists);
                    setShowValidationError(false);
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    const exists = vesselNameExists(val);
                    setShowNameError(!val);
                    setShowVesselNameExistError(exists);
                  }}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="add_type">Vessel Type:</label>
                <select
                  className="form-select form-select-sm"
                  id="add_type"
                  name="add_type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="SHIP">SHIP</option>
                  <option value="TRAIN">TRAIN</option>
                  <option value="TRUCK">TRUCK</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="add_status">Status:</label>
                <select
                  className="form-select form-select-sm"
                  id="add_status"
                  name="add_status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <Button
                  type="button"
                  className="btn btn-inox-green border-0 canvas-button btn-sm mt-4"
                  onClick={(e) => addVessel(e)}
                  disabled={hasAnyAddError() || showAddLoader}
                >
                  {showAddLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-plus"></i>
                      <span className="mx-2">Add</span>
                    </>
                  )}
                </Button>

                {(hasAnyAddError() || showValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{
                        backgroundColor: "#C21E22",
                        border: "#f82e2e",
                      }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleCheckValidationClick}
                      onMouseLeave={() => setIsValidationErrorVisible(false)}
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {buildAddErrorMessages().map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* -------------------- End Add Canvas -------------------- */}
    </div>
  );
}

export default VesselMaster;
